/**
 * Funding Blueprint Funnel Page
 * ClickFunnels-style multi-step funnel landing page
 */

import React, { useState, useEffect } from "react";
import { 
  ArrowRight, 
  CheckCircle, 
  Shield,
  Clock,
  Zap,
  ChevronDown,
  Star,
  AlertCircle,
  XCircle,
  ArrowDown,
  Lock,
  Gift,
  Play,
  Users,
  TrendingUp,
  CreditCard,
  FileText,
  ChevronRight,
  Target,
  BarChart3,
  Building,
  DollarSign,
  Percent,
  Award,
  Sparkles,
  PieChart,
  Layers
} from "lucide-react";

const BOOK_COVER = "https://static.prod-images.emergentagent.com/jobs/d1fab6bb-fd4d-49ee-a7e3-3244d7a569c8/images/eaa3cea047f21671dadc08d2de50423e32f78f22d263e12391c218e8285f85f3.png";

// Card images from major lenders
const LENDER_LOGOS = {
  chase: "https://lsrooqwnlekfgeldzrpk.supabase.co/storage/v1/object/public/card-images/chase/effddc1c-3bb8-45e2-bb85-59e366f4d121.png",
  amex: "https://lsrooqwnlekfgeldzrpk.supabase.co/storage/v1/object/public/card-images/american-express/1fb262ae-1f35-4bb8-b5f3-1bd764f58c6e.webp",
  capitalOne: "https://lsrooqwnlekfgeldzrpk.supabase.co/storage/v1/object/public/card-images/capital-one/2f7bfba3-4265-4aaf-8a26-35b2a8345cd8.jpg",
  usBank: "https://lsrooqwnlekfgeldzrpk.supabase.co/storage/v1/object/public/card-images/u-s-bank/97251a98-7a09-462b-9f0d-1502c7f700ce.jpg",
  chaseUnlimited: "https://lsrooqwnlekfgeldzrpk.supabase.co/storage/v1/object/public/card-images/chase/6a92e3ef-b36a-4cd1-841a-4f501c9b9af6.png"
};

const testimonials = [
  { name: "Marcus T.", business: "Home Remodeling", result: "$87,000", quote: "Got approved for 6 cards in 3 weeks!", rating: 5, location: "Dallas, TX" },
  { name: "Sarah K.", business: "E-commerce", result: "$124,000", quote: "The phased approach was genius.", rating: 5, location: "Miami, FL" },
  { name: "David R.", business: "Landscaping", result: "$63,000", quote: "Finally got the capital I needed.", rating: 5, location: "Phoenix, AZ" },
  { name: "Jennifer M.", business: "Consulting", result: "$95,000", quote: "Couldn't believe how easy it was.", rating: 5, location: "Atlanta, GA" },
  { name: "Robert L.", business: "Construction", result: "$142,000", quote: "Best investment I've ever made.", rating: 5, location: "Houston, TX" },
  { name: "Amanda C.", business: "Marketing Agency", result: "$78,000", quote: "The lender matching was spot on.", rating: 5, location: "Chicago, IL" }
];

const problems = [
  "Getting DENIED for business credit over and over",
  "Watching your credit score DROP with every application", 
  "Not knowing which cards to apply for first",
  "Paying RIDICULOUS interest rates on business loans",
  "Giving up EQUITY just to get working capital",
  "Feeling STUCK while competitors grow past you"
];

const matchedLenders = [
  { name: "Chase Ink Business Preferred", type: "Business Credit Card", approval: "96%", amount: "$25,000", logo: LENDER_LOGOS.chase },
  { name: "Amex Blue Business Plus", type: "0% APR Business Card", approval: "93%", amount: "$18,000", logo: LENDER_LOGOS.amex },
  { name: "Capital One Spark Cash", type: "Cash Back Business Card", approval: "89%", amount: "$15,000", logo: LENDER_LOGOS.capitalOne },
  { name: "Chase Ink Business Unlimited", type: "Business Credit Card", approval: "87%", amount: "$20,000", logo: LENDER_LOGOS.chaseUnlimited },
  { name: "U.S. Bank Triple Cash", type: "Business Rewards Card", approval: "84%", amount: "$12,000", logo: LENDER_LOGOS.usBank }
];

const included = [
  { title: "Personalized Credit Analysis", value: "$497" },
  { title: "Custom Lender Match Report", value: "$397" },
  { title: "Phased Application Strategy", value: "$597" },
  { title: "Approval Odds Calculator", value: "$297" },
  { title: "Reconsideration Scripts", value: "$347" },
  { title: "Step-by-Step Action Plan", value: "$297" },
  { title: "30-Day Implementation Guide", value: "$297" },
  { title: "Email Support Access", value: "$497" }
];

export default function BlueprintFunnel() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    phone: '',
    businessType: '',
    monthlyRevenue: '',
    creditScore: ''
  });
  const [timeLeft, setTimeLeft] = useState({ hours: 11, minutes: 47, seconds: 33 });
  const [openFaq, setOpenFaq] = useState(0);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { hours, minutes, seconds } = prev;
        seconds--;
        if (seconds < 0) { seconds = 59; minutes--; }
        if (minutes < 0) { minutes = 59; hours--; }
        if (hours < 0) { hours = 23; minutes = 59; seconds = 59; }
        return { hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const faqs = [
    { q: "What exactly do I get?", a: "You get a 100% personalized funding strategy document showing exactly which credit cards to apply for, in what order, and when — all based on YOUR specific credit profile. Plus reconsideration scripts, approval odds, and a step-by-step action plan." },
    { q: "How much funding can I get?", a: "Most clients secure $50,000 to $150,000 in 0% APR business credit within 30 days. Some have gotten over $200K. Your Blueprint will show your specific funding potential." },
    { q: "Will this hurt my credit?", a: "Our phased approach minimizes inquiry impact by strategically timing applications across different bureaus. Most clients see only a 10-15 point temporary dip." },
    { q: "What if I get denied?", a: "Your Blueprint includes word-for-word reconsideration scripts. We show you exactly what to say to turn denials into approvals." },
    { q: "Is there a guarantee?", a: "Yes! If you follow the Blueprint and don't get approved for at least ONE card in 30 days, we'll refund 100% of your purchase. No questions asked." }
  ];

  // Step 1: Opt-in / Problem Aware
  const renderStep1 = () => (
    <>
      {/* Hero */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#0f2d52] via-[#0f2d52] to-[#1a3a5c]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left - Copy */}
            <div className="text-center lg:text-left">
              <p className="text-[#48d480] font-bold text-sm md:text-base mb-4 uppercase tracking-wider">
                AI-Powered Credit Stacking Strategy
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight mb-6">
                Get Your <span className="text-[#48d480]">Personalized</span> Business Funding Blueprint
                <span className="block text-2xl md:text-3xl mt-2 text-white/80">Up to $150K in 0% APR Credit</span>
              </h1>
              <p className="text-lg md:text-xl text-white/70 mb-8 leading-relaxed">
                See exactly which lenders will approve you, your approval odds for each, 
                and the perfect application sequence — all customized to YOUR credit profile.
              </p>

              {/* Opt-in Form */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <p className="text-white font-bold text-lg mb-4">
                  Get Your Free Blueprint Analysis 👇
                </p>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="Your First Name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 rounded-lg bg-white text-gray-800 font-medium placeholder-gray-400 focus:ring-2 focus:ring-[#48d480] focus:outline-none"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Best Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 rounded-lg bg-white text-gray-800 font-medium placeholder-gray-400 focus:ring-2 focus:ring-[#48d480] focus:outline-none"
                  />
                  <button 
                    onClick={nextStep}
                    className="w-full bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-lg py-4 rounded-lg transition-all transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-2"
                  >
                    YES! Show Me My Blueprint <ArrowRight size={20} />
                  </button>
                </div>
                <p className="text-white/50 text-xs mt-4 flex items-center justify-center gap-2">
                  <Lock size={12} /> Your info is safe. We hate spam too.
                </p>
              </div>
            </div>

            {/* Right - Book Image */}
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="overflow-hidden rounded-lg">
                  <img 
                    src={BOOK_COVER} 
                    alt="Business Funding Blueprint" 
                    className="w-80 drop-shadow-2xl transform hover:scale-105 transition-transform duration-500 scale-110"
                    style={{ marginTop: '-5%', marginBottom: '-5%' }}
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-[#48d480] text-white font-bold px-4 py-2 rounded-lg shadow-lg">
                  AI-ANALYZED
                </div>
              </div>
            </div>
          </div>

          {/* Trust Bar */}
          <div className="flex flex-wrap justify-center gap-8 mt-12 pt-8 border-t border-white/10">
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-black text-[#48d480]">$2.5M+</p>
              <p className="text-white/60 text-sm">Funding Secured</p>
            </div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-black text-[#48d480]">500+</p>
              <p className="text-white/60 text-sm">Blueprints Created</p>
            </div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-black text-[#48d480]">74%</p>
              <p className="text-white/60 text-sm">Avg Approval Rate</p>
            </div>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />)}
            </div>
          </div>
        </div>
      </section>

      {/* Blueprint Preview Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#0f2d52]/10 text-[#0f2d52] text-xs font-bold px-4 py-2 rounded-full mb-4">
              WHAT YOU'LL DISCOVER
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-[#0f2d52] mb-4">
              Your Personalized Funding Roadmap
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              See a preview of what your custom Blueprint reveals about your funding potential
            </p>
          </div>

          {/* Blueprint Preview Cards */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-gradient-to-br from-[#0f2d52] to-[#1a4a7a] rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <Target size={32} className="text-[#48d480]" />
              </div>
              <p className="text-4xl font-black text-[#48d480] mb-1">73+</p>
              <p className="text-white/70 text-sm">Funding Readiness Score</p>
            </div>
            <div className="bg-gradient-to-br from-[#0f2d52] to-[#1a4a7a] rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <Building size={32} className="text-[#48d480]" />
              </div>
              <p className="text-4xl font-black text-[#48d480] mb-1">11+</p>
              <p className="text-white/70 text-sm">Lenders Matched</p>
            </div>
            <div className="bg-gradient-to-br from-[#0f2d52] to-[#1a4a7a] rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <Percent size={32} className="text-[#48d480]" />
              </div>
              <p className="text-4xl font-black text-[#48d480] mb-1">96%</p>
              <p className="text-white/70 text-sm">Top Approval Odds</p>
            </div>
            <div className="bg-gradient-to-br from-[#0f2d52] to-[#1a4a7a] rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <DollarSign size={32} className="text-[#48d480]" />
              </div>
              <p className="text-4xl font-black text-[#48d480] mb-1">$50K+</p>
              <p className="text-white/70 text-sm">Estimated Funding</p>
            </div>
          </div>

          {/* Funding Scenarios */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-[#0f2d52] text-center mb-8">
              Your Funding Scenarios
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 text-center border-2 border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Conservative</p>
                <p className="text-4xl font-black text-gray-600">$25K</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center border-2 border-[#48d480] shadow-lg relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#48d480] text-white text-xs font-bold px-3 py-1 rounded-full">
                  MOST LIKELY
                </div>
                <p className="text-sm text-gray-500 mb-2">Realistic</p>
                <p className="text-5xl font-black text-[#0f2d52]">$50K</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center border-2 border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Optimal</p>
                <p className="text-4xl font-black text-[#48d480]">$150K</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Matched Lenders Preview */}
      <section className="py-16 bg-[#0f2d52]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Preview: Your Matched Lenders
            </h2>
            <p className="text-white/60">
              Your Blueprint includes 11+ lenders matched to your profile with approval odds
            </p>
          </div>

          <div className="space-y-4 mb-8">
            {matchedLenders.map((lender, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center justify-between border border-white/10">
                <div className="flex items-center gap-4">
                  <img src={lender.logo} alt={lender.name} className="w-16 h-10 object-contain bg-white rounded-lg p-1" />
                  <div>
                    <p className="text-white font-bold">{lender.name}</p>
                    <p className="text-white/50 text-sm">{lender.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-[#48d480] font-black text-xl">{lender.approval}</p>
                    <p className="text-white/40 text-xs">Approval Odds</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white font-bold text-xl">{lender.amount}</p>
                    <p className="text-white/40 text-xs">Est. Limit</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-white/50 mb-4">+ 6 more lenders in your full Blueprint</p>
            <button 
              onClick={nextStep}
              className="bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-xl px-10 py-5 rounded-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Unlock My Full Blueprint <ArrowRight size={20} className="inline ml-2" />
            </button>
          </div>
        </div>
      </section>

      {/* Problem Agitation */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-black text-center text-gray-800 mb-4">
            Without A Strategy, You're Gambling
          </h2>
          <p className="text-center text-gray-500 text-lg mb-10">
            Most business owners waste time, damage their credit, and miss out on funding because they don't have a plan:
          </p>
          <div className="space-y-4">
            {problems.map((problem, idx) => (
              <div key={idx} className="flex items-center gap-4 bg-red-50 p-5 rounded-xl border-l-4 border-red-400">
                <XCircle size={24} className="text-red-500 flex-shrink-0" />
                <span className="text-gray-700 text-lg font-medium">{problem}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-xl font-bold text-[#0f2d52] mb-6">
              Your Blueprint eliminates the guesswork. <span className="text-[#48d480]">Get funded faster.</span>
            </p>
            <button 
              onClick={nextStep}
              className="bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-xl px-10 py-5 rounded-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Show Me The Solution <ArrowRight size={20} className="inline ml-2" />
            </button>
          </div>
        </div>
      </section>

      {/* Why It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-[#0f2d52] mb-4">
              Why The Blueprint Works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-[#48d480]/10 flex items-center justify-center mb-4">
                <Sparkles size={28} className="text-[#48d480]" />
              </div>
              <h3 className="text-xl font-bold text-[#0f2d52] mb-3">AI-Powered Matching</h3>
              <p className="text-gray-600">
                Our AI analyzes your credit profile against 50+ lender criteria to find your best matches 
                with the highest approval odds.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-[#48d480]/10 flex items-center justify-center mb-4">
                <Layers size={28} className="text-[#48d480]" />
              </div>
              <h3 className="text-xl font-bold text-[#0f2d52] mb-3">Strategic Stacking</h3>
              <p className="text-gray-600">
                The credit stacking strategy unlocks multiple funding sources simultaneously — faster capital 
                deployment than traditional single-lender approaches.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-[#48d480]/10 flex items-center justify-center mb-4">
                <Shield size={28} className="text-[#48d480]" />
              </div>
              <h3 className="text-xl font-bold text-[#0f2d52] mb-3">Credit Protection</h3>
              <p className="text-gray-600">
                Our phased approach times applications across bureaus strategically, minimizing inquiry impact 
                while maximizing approvals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-black text-center text-[#0f2d52] mb-10">
            Real Results From Real Business Owners
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className="flex gap-1 mb-3">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-gray-600 mb-4">"{t.quote}"</p>
                <div className="border-t pt-4">
                  <p className="font-bold text-[#0f2d52]">{t.name}</p>
                  <p className="text-gray-500 text-sm">{t.business} • {t.location}</p>
                  <p className="text-[#48d480] font-bold text-xl mt-2">{t.result}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <button 
              onClick={nextStep}
              className="bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-xl px-10 py-5 rounded-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Get My Blueprint Now <ArrowRight size={20} className="inline ml-2" />
            </button>
          </div>
        </div>
      </section>

      {/* Time Sensitive */}
      <section className="py-12 bg-[#0f2d52]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="bg-[#e8821c]/20 border border-[#e8821c]/40 rounded-xl p-6 mb-6">
            <AlertCircle size={32} className="text-[#e8821c] mx-auto mb-3" />
            <h3 className="text-xl font-bold text-white mb-2">Time-Sensitive Opportunity</h3>
            <p className="text-white/70">
              Prime lending windows close as rate adjustments take effect. 
              Your approval window narrows each week rates shift.
            </p>
          </div>
          <button 
            onClick={nextStep}
            className="bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-xl px-10 py-5 rounded-lg transition-all transform hover:scale-105 shadow-xl"
          >
            Claim My Blueprint Before Rates Change <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>
    </>
  );

  // Step 2: Solution / Value
  const renderStep2 = () => (
    <>
      {/* Solution Hero */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#0f2d52] to-[#1a3a5c]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#48d480] font-bold mb-4">STEP 2 OF 3</p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6">
            Here's Your <span className="text-[#48d480]">Personalized Solution</span>
          </h1>
          <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
            The Business Funding Blueprint gives you a step-by-step strategy 
            customized to YOUR credit profile and business.
          </p>
          
          {/* Book + Checklist */}
          <div className="grid md:grid-cols-2 gap-8 items-center mt-12">
            <div className="flex justify-center">
              <div className="overflow-hidden rounded-lg">
                <img src={BOOK_COVER} alt="Blueprint" className="w-64 md:w-72 drop-shadow-2xl scale-110" style={{ marginTop: '-5%', marginBottom: '-5%' }} />
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-left">
              <h3 className="font-bold text-xl text-[#0f2d52] mb-4">What's Inside:</h3>
              <div className="space-y-3">
                {[
                  "Personalized lender recommendations",
                  "Which credit bureau each pulls",
                  "Your approval odds for each card",
                  "Exact application sequence",
                  "Same-day stacking strategy",
                  "Reconsideration scripts",
                  "30-day action plan"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-[#48d480]" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-black text-center text-[#0f2d52] mb-12">
            The 3-Phase Strategy
          </h2>
          <div className="space-y-6">
            {[
              { phase: "1", title: "Equifax Lenders", week: "Week 1", desc: "Start with lenders pulling Equifax to establish your foundation." },
              { phase: "2", title: "TransUnion Lenders", week: "Week 2", desc: "Move to TransUnion lenders while Equifax inquiries don't affect them." },
              { phase: "3", title: "Experian Lenders", week: "Week 4", desc: "Complete your stack with Experian lenders for maximum funding." }
            ].map((step, idx) => (
              <div key={idx} className="flex gap-6 items-start bg-gray-50 rounded-xl p-6">
                <div className="w-14 h-14 rounded-full bg-[#48d480] text-white font-black text-xl flex items-center justify-center flex-shrink-0">
                  {step.phase}
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-bold text-xl text-[#0f2d52]">{step.title}</h3>
                    <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">{step.week}</span>
                  </div>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Phase Disclaimer */}
          <p className="text-center text-gray-400 text-xs mt-6 italic">
            *Phases and timelines shown are for illustrative purposes only. Actual strategy, lender recommendations, and application timing will vary based on each client's unique credit profile, financial situation, and lending requirements.
          </p>
          
          <div className="text-center mt-12">
            <button 
              onClick={nextStep}
              className="bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-xl px-10 py-5 rounded-lg transition-all transform hover:scale-105 shadow-xl"
            >
              I Want My Blueprint! <ArrowRight size={20} className="inline ml-2" />
            </button>
            <button onClick={prevStep} className="block mx-auto mt-4 text-gray-500 hover:text-gray-700">
              ← Go Back
            </button>
          </div>
        </div>
      </section>
    </>
  );

  // Step 3: Offer / Checkout
  const renderStep3 = () => (
    <>
      {/* Urgency Bar */}
      <div className="bg-red-600 text-white py-3 text-center">
        <p className="font-bold flex items-center justify-center gap-2">
          <AlertCircle size={18} />
          Special Pricing Expires In: {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
        </p>
      </div>

      {/* Checkout Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-[#0f2d52] to-[#1a3a5c]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-[#48d480] font-bold mb-2">FINAL STEP</p>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-4">
              Get Your Blueprint Now
            </h1>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left - Book + What's Included */}
            <div>
              <div className="flex justify-center mb-8">
                <div className="overflow-hidden rounded-lg bg-[#0f2d52]">
                  <img src={BOOK_COVER} alt="Blueprint" className="w-48 drop-shadow-2xl scale-110" style={{ marginTop: '-5%', marginBottom: '-5%' }} />
                </div>
              </div>
              
              <div className="bg-white/10 rounded-xl p-6 border border-white/20">
                <h3 className="text-white font-bold text-lg mb-4">Here's Everything You Get:</h3>
                <div className="space-y-3">
                  {included.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-white/10 last:border-0">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-[#48d480]" />
                        <span className="text-white/90">{item.title}</span>
                      </div>
                      <span className="text-white/50 line-through text-sm">{item.value}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-4 pt-4 border-t border-white/20">
                  <span className="text-white font-bold">Total Value:</span>
                  <span className="text-white/50 line-through text-xl">$3,226</span>
                </div>
              </div>
            </div>

            {/* Right - Pricing Box */}
            <div>
              <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-[#48d480]">
                <div className="text-center mb-6">
                  <p className="text-gray-500 line-through text-lg">Regular Price: $2,997</p>
                  <p className="text-5xl md:text-6xl font-black text-[#0f2d52]">$1,497</p>
                  <p className="text-[#48d480] font-bold">One-Time Payment</p>
                </div>

                {/* Form Fields */}
                <div className="space-y-4 mb-6">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#48d480] focus:outline-none"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#48d480] focus:outline-none"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#48d480] focus:outline-none"
                  />
                </div>

                <a
                  href="https://app.trulli.ai/marketplace/legacytriangle"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-xl py-5 rounded-lg transition-all text-center shadow-lg hover:shadow-xl"
                >
                  GET INSTANT ACCESS →
                </a>

                <div className="flex flex-wrap justify-center gap-4 mt-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><Lock size={14} /> Secure</span>
                  <span className="flex items-center gap-1"><Shield size={14} /> 30-Day Guarantee</span>
                </div>
              </div>

              {/* Guarantee Box */}
              <div className="bg-[#0f2d52] rounded-xl p-5 mt-6 flex gap-4">
                <Gift size={32} className="text-[#48d480] flex-shrink-0" />
                <div>
                  <h4 className="text-white font-bold mb-1">100% Money-Back Guarantee</h4>
                  <p className="text-white/70 text-sm">
                    Get approved for at least 1 card in 30 days or get a full refund.
                  </p>
                </div>
              </div>

              <button onClick={prevStep} className="block mx-auto mt-6 text-white/50 hover:text-white">
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl font-black text-center text-[#0f2d52] mb-8">
            Questions? We've Got Answers.
          </h2>
          <div className="space-y-2">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border-b border-gray-200">
                <button 
                  onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                  className="w-full py-4 flex items-center justify-between text-left"
                >
                  <span className="font-bold text-[#0f2d52]">{faq.q}</span>
                  <ChevronDown size={20} className={`text-[#48d480] transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === idx && <p className="pb-4 text-gray-600">{faq.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 bg-[#0f2d52] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">
            Stop Guessing. Start Stacking.
          </h2>
          <p className="text-white/70 mb-6">Your personalized funding blueprint is waiting.</p>
          <a
            href="https://app.trulli.ai/marketplace/legacytriangle"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#48d480] hover:bg-[#3ab370] text-white font-bold text-xl px-10 py-5 rounded-lg transition-all shadow-xl"
          >
            GET MY BLUEPRINT NOW →
          </a>
          <p className="text-white/40 text-sm mt-8">
            © {new Date().getFullYear()} Legacy Triangle. All rights reserved.
          </p>
        </div>
      </section>
    </>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="blueprint-funnel-page">
      {/* Progress Bar */}
      <div className="bg-[#0a1f3d] py-3">
        <div className="max-w-md mx-auto px-4">
          <div className="flex items-center justify-between text-white text-sm mb-2">
            <span>Step {currentStep} of 3</span>
            <span>{Math.round((currentStep / 3) * 100)}% Complete</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#48d480] transition-all duration-500"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Render Current Step */}
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}

      {/* Footer with Disclosures */}
      <footer className="bg-[#0a1a2e] py-12 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4">
          {/* Disclosures */}
          <div className="text-white/40 text-xs leading-relaxed space-y-4">
            <p className="font-semibold text-white/60 text-sm mb-4">Important Disclosures</p>
            
            <p>
              <strong>Results Disclaimer:</strong> The results and funding amounts mentioned on this page are examples of what some clients have achieved. Individual results will vary based on your credit profile, business history, income, and other factors. We do not guarantee any specific funding amount or approval rate. Past performance is not indicative of future results.
            </p>
            
            <p>
              <strong>No Guarantee of Approval:</strong> Legacy Triangle does not guarantee approval for any credit card, loan, or line of credit. All credit decisions are made by third-party lenders based on their own underwriting criteria. We provide educational information and strategies, but approval is never guaranteed.
            </p>
            
            <p>
              <strong>Credit Impact:</strong> Applying for credit may result in hard inquiries on your credit report, which can temporarily lower your credit score. While our phased approach is designed to minimize inquiry impact, we cannot guarantee the effect on your individual credit score. Consult with a financial advisor before making credit decisions.
            </p>
            
            <p>
              <strong>Educational Purposes:</strong> The information provided in the Business Funding Blueprint is for educational and informational purposes only. It is not intended as financial, legal, or tax advice. You should consult with qualified professionals regarding your specific situation.
            </p>
            
            <p>
              <strong>Third-Party Lenders:</strong> Legacy Triangle is not a lender. We do not make credit decisions or fund loans. All lending products are offered by third-party financial institutions. Terms, rates, and availability are subject to change without notice.
            </p>
            
            <p>
              <strong>Income & Earnings Disclaimer:</strong> Any income or earnings statements are estimates of what may be possible. There is no assurance you will achieve similar results. Your success depends on many factors including your credit, effort, and market conditions.
            </p>
            
            <p>
              <strong>Refund Policy:</strong> Our 30-day money-back guarantee is subject to terms and conditions. To qualify for a refund, you must demonstrate that you followed the Blueprint strategy and applied to at least the recommended lenders within 30 days of purchase.
            </p>
          </div>
          
          {/* Links */}
          <div className="flex flex-wrap justify-center gap-6 mt-8 pt-8 border-t border-white/10 text-white/40 text-xs">
            <a href="/privacy-policy" className="hover:text-white/60 transition-colors">Privacy Policy</a>
            <a href="/terms-of-service" className="hover:text-white/60 transition-colors">Terms of Service</a>
            <a href="/disclaimer" className="hover:text-white/60 transition-colors">Disclaimer</a>
            <a href="/contact" className="hover:text-white/60 transition-colors">Contact Us</a>
          </div>
          
          {/* Copyright */}
          <p className="text-center text-white/30 text-xs mt-6">
            © {new Date().getFullYear()} Legacy Triangle. All rights reserved. This site is not a part of the Facebook website or Facebook Inc. Additionally, this site is NOT endorsed by Facebook in any way. FACEBOOK is a trademark of FACEBOOK, Inc.
          </p>
        </div>
      </footer>
    </div>
  );
}
